import { st, classes, cssStates } from './WsrBaseCalendar.st.css';
import React from 'react';
import PropTypes from 'prop-types';
import DayPicker from 'react-day-picker-wsr';
import localeUtilsFactory from '../../common/LocaleUtils/LocaleUtils';
import { WixStyleReactEnvironmentContext, } from '../../WixStyleReactEnvironmentProvider/context';
import { supportedWixlocales } from 'wix-design-systems-locale-utils';
// https://daypicker.dev/v8/upgrading
// https://classic.yarnpkg.com/en/docs/cli/add/#toc-yarn-add-alias
const getIsRange = (value) => {
    if (!value || value instanceof Date) {
        return false;
    }
    return true;
};
class BaseCalendar extends React.PureComponent {
    constructor() {
        super(...arguments);
        this._renderDay = (day, modifiers) => {
            const { dateIndication } = this.props;
            const isOutsideDay = !!modifiers[cssStates({ outside: true })];
            const isSelectedDay = !!modifiers[cssStates({ selected: true })];
            const dateIndicationNode = dateIndication?.({
                date: day,
                isSelected: isSelectedDay,
            });
            const hasIndication = dateIndicationNode && !isOutsideDay;
            return (React.createElement("div", { className: st(classes.dayWrapper, { hasIndication }), "data-date": `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`, "data-outsideday": isOutsideDay },
                React.createElement("div", { className: classes.dayText }, day.getDate()),
                hasIndication ? (React.createElement("div", { className: classes.dayIndicationContainer }, dateIndicationNode)) : null));
        };
        this._handleDayClick = (value, modifiers = { outside: undefined, today: undefined }, event = null) => {
            this._preventActionEventDefault(event);
            const { onChange, shouldCloseOnSelect } = this.props;
            if (this.props.selectionMode === 'range') {
                const propsValue = this.props.value || {};
                if (
                /* both undefined and type safe */ !getIsRange(propsValue) ||
                    /* both undefined */ (!propsValue.from && !propsValue.to) ||
                    /* both defined */ (propsValue.from && propsValue.to)) {
                    onChange({ from: value }, modifiers);
                }
                else {
                    const anchor = propsValue.from || propsValue.to;
                    const newVal = anchor < value
                        ? { from: anchor, to: value }
                        : { from: value, to: anchor };
                    onChange(newVal, modifiers);
                    shouldCloseOnSelect && this.props.onClose(event);
                }
            }
            else {
                onChange(value, modifiers);
                shouldCloseOnSelect && this.props.onClose(event);
            }
        };
        this._preventActionEventDefault = (event = null) => {
            if (event &&
                (!event.key || (event.key !== 'Escape' && event.key !== 'Tab'))) {
                event.preventDefault();
            }
        };
        this._createWeekdayElement = (localeUtils) => {
            return ({ className, weekday }) => {
                const weekdayShort = localeUtils.formatWeekdayShort(weekday);
                const weekdayLong = localeUtils.formatWeekdayLong(weekday);
                return (React.createElement("div", { className: className, "aria-label": weekdayLong, role: "columnheader" },
                    React.createElement("abbr", { "data-hook": "weekday-day" }, weekdayShort)));
            };
        };
        this._createDayPickerProps = () => {
            const { filterDate, excludePastDates, numOfMonths, firstDayOfWeek, rtl, today, onDisplayedViewChange, displayedMonth, captionElement, allowSelectingOutsideDays, size, fixedWeeks, } = this.props;
            const locale = this._getLocale();
            const value = BaseCalendar.parseValue(this.props.value);
            const localeUtils = localeUtilsFactory(locale, firstDayOfWeek);
            const range = getIsRange(value) ? value : {};
            const { from, to } = range;
            const singleDay = !getIsRange(value) && value;
            const firstOfMonth = [
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1),
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1),
            ];
            const lastOfMonth = [
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 0),
                new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 2, 0),
            ];
            const selectedDays = this._getSelectedDays(value);
            const weekdayElement = this._createWeekdayElement(localeUtils);
            const modifiers = {
                [cssStates({ start: true })]: from,
                [cssStates({ end: true })]: to,
                [cssStates({ firstOfMonth: true })]: firstOfMonth,
                [cssStates({ lastOfMonth: true })]: lastOfMonth,
                [cssStates({ singleDay: true })]: singleDay,
                [cssStates({ past: true })]: { before: BaseCalendar.parseValue(today) },
                ...this.props.modifiers,
            };
            if (today) {
                modifiers[cssStates({ today: true })] = BaseCalendar.parseValue(today);
            }
            // We must add the dummy state since ReactDayPicker use it as a selector in their code
            const outsideCssState = allowSelectingOutsideDays
                ? cssStates({ dummyOutside: true })
                : cssStates({ outside: true });
            return {
                disabledDays: new Array((date) => !filterDate(new Date(date)), excludePastDates ? { before: new Date() } : undefined).filter(Boolean),
                initialMonth: displayedMonth,
                // @ts-expect-error
                initialYear: displayedMonth,
                // eslint-disable-next-line object-shorthand
                selectedDays: selectedDays,
                month: displayedMonth,
                year: displayedMonth,
                locale: typeof locale === 'string' ? locale : '',
                fixedWeeks,
                onKeyDown: this._handleKeyDown,
                onDayClick: this._handleDayClick,
                localeUtils,
                navbarElement: () => null,
                captionElement,
                onCaptionClick: this._preventActionEventDefault,
                onDayKeyDown: this._handleDayKeyDown,
                numberOfMonths: numOfMonths,
                modifiers,
                renderDay: this._renderDay,
                dir: rtl ? 'rtl' : 'ltr',
                tabIndex: 0,
                weekdayElement,
                classNames: {
                    caption: undefined,
                    footer: undefined,
                    navBar: undefined,
                    navButtonPrev: undefined,
                    navButtonNext: undefined,
                    navButtonInteractionDisabled: undefined,
                    todayButton: undefined,
                    /* The classes: 'DayPicker', 'DayPicker-wrapper', 'DayPicker-Month', 'DayPicker-Day', 'disabled'
                    are used as selectors for the elements at the drivers and at the e2e tests */
                    container: st(classes.container, { size }, 'DayPicker'),
                    wrapper: 'DayPicker-wrapper',
                    interactionDisabled: 'DayPicker--interactionDisabled',
                    months: st(classes.months, { twoMonths: numOfMonths > 1 }),
                    month: st(classes.month, { size }, 'DayPicker-Month'),
                    weekdays: classes.weekdays,
                    weekdaysRow: classes.weekdaysRow,
                    weekday: st(classes.weekday, { size }),
                    body: st(classes.body, { size }),
                    week: classes.week,
                    weekNumber: 'DayPicker-WeekNumber',
                    day: st(classes.day, { size }, 'DayPicker-Day'),
                    // default modifiers
                    today: cssStates({ today: !today }),
                    selected: cssStates({ selected: true }),
                    disabled: st('disabled', cssStates({ disabled: true })),
                    outside: outsideCssState,
                },
                onMonthChange: onDisplayedViewChange,
            };
        };
        this._handleKeyDown = (event) => {
            const { onKeyDown } = this.props;
            if (onKeyDown) {
                onKeyDown(event);
                return;
            }
            if (event.key === 'Escape') {
                this.props.onClose(event);
            }
        };
        this._query = (selector) => this.dayPickerRef.dayPicker.querySelector(selector);
        this._toggleFirstDayTabIndex = (tabIndex) => {
            const firstDay = this._query(`.DayPicker-Day[tabindex="${tabIndex}"]`);
            firstDay.tabIndex = tabIndex === 0 ? -1 : 0;
        };
        this._focusSelectedDay = () => {
            if (this.dayPickerRef) {
                const selectedDay = this._query(`.${cssStates({ selected: true })}`);
                if (selectedDay) {
                    // The 'unfocused' class is used as a selector at the drivers and e2e test
                    selectedDay.classList.add(cssStates({ unfocused: true }), 'unfocused');
                    selectedDay.tabIndex = 0;
                    selectedDay.focus();
                    this._toggleFirstDayTabIndex(0);
                }
                else {
                    this._toggleFirstDayTabIndex(-1);
                }
            }
        };
        this._handleDayKeyDown = (_value, _modifiers, event = null) => {
            this._preventActionEventDefault(event);
            const unfocusedDay = this._query(`.${cssStates({ unfocused: true })}`);
            if (unfocusedDay) {
                // The 'unfocused' class is used as a selector at the drivers and e2e test
                unfocusedDay.classList.remove(cssStates({ unfocused: true }), 'unfocused');
            }
        };
    }
    static parseValue(value) {
        if (!value) {
            return new Date();
        }
        if (value instanceof Date) {
            return value;
        }
        else {
            return {
                from: value.from,
                to: value.to,
            };
        }
    }
    _getSelectedDays(value) {
        const range = getIsRange(value) ? value : {};
        const { from, to } = range;
        if (from && to) {
            return { from, to };
        }
        else if (from) {
            return { after: BaseCalendar.prevDay(from) };
        }
        else if (to) {
            return { before: BaseCalendar.nextDay(to) };
        }
        else {
            // Single day OR empty value
            return value;
        }
    }
    _getLocale() {
        return this.props.locale || this.context.locale || 'en';
    }
    componentDidMount() {
        this.props.autoFocus && this._focusSelectedDay();
        if (this.dayPickerRef) {
            // @ts-expect-error TODO: JB: nothing to do here, external type
            this.dayPickerRef.wrapper.tabIndex = -1;
        }
    }
    componentDidUpdate(prevProps) {
        if (!prevProps.autoFocus && this.props.autoFocus) {
            this._focusSelectedDay();
        }
    }
    render() {
        const { dataHook, className, size } = this.props;
        return (React.createElement("div", { "data-hook": dataHook, "data-size": size, className: st(classes.root, className), onClick: this._preventActionEventDefault, role: "dialog", tabIndex: -1 },
            React.createElement(DayPicker, { ref: (ref) => (this.dayPickerRef = ref), ...this._createDayPickerProps() })));
    }
}
BaseCalendar.displayName = 'BaseCalendar';
BaseCalendar.defaultProps = {
    className: '',
    filterDate: () => true,
    dateIndication: () => null,
    shouldCloseOnSelect: true,
    onClose: () => { },
    autoFocus: true,
    excludePastDates: false,
    selectionMode: 'day',
    // @ts-expect-error
    showMonthDropdown: false,
    showYearDropdown: false,
    numOfMonths: 1,
    size: 'medium',
    allowSelectingOutsideDays: false,
    fixedWeeks: true,
};
BaseCalendar.nextDay = (date) => {
    const day = new Date(date);
    day.setDate(day.getDate() + 1);
    return day;
};
BaseCalendar.prevDay = (date) => {
    const day = new Date(date);
    day.setDate(day.getDate() - 1);
    return day;
};
export default BaseCalendar;
BaseCalendar.contextType = WixStyleReactEnvironmentContext;
BaseCalendar.propTypes = {
    dataHook: PropTypes.string,
    autoFocus: PropTypes.bool,
    numOfMonths: PropTypes.oneOf([1, 2]),
    firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func,
    onKeyDown: PropTypes.func,
    excludePastDates: PropTypes.bool,
    filterDate: PropTypes.func,
    value: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.shape({
            from: PropTypes.instanceOf(Date),
            to: PropTypes.instanceOf(Date),
        }),
    ]),
    selectionMode: PropTypes.oneOf(['day', 'range']),
    shouldCloseOnSelect: PropTypes.bool,
    locale: PropTypes.oneOfType([PropTypes.oneOf(supportedWixlocales)]),
    rtl: PropTypes.bool,
    dateIndication: PropTypes.func,
    today: PropTypes.instanceOf(Date),
    displayedMonth: PropTypes.instanceOf(Date).isRequired,
    onDisplayedViewChange: PropTypes.func.isRequired,
    // @ts-expect-error TODO: JB: propTypes
    captionElement: PropTypes.elementType.isRequired,
    modifiers: PropTypes.object,
    allowSelectingOutsideDays: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium']),
};
//# sourceMappingURL=BaseCalendar.js.map